import type { V2 } from '../core/math';

/**
 * Getting into a car, the pure part (docs/CONTROLS-POLISH.md): which door, and the path to it round
 * the bodywork. Everything is in the car's own frame — `lat` along its right (+ = the driver's side,
 * UK cars are right-hand drive), `al` along its nose — so a path keeps up with a car that is still
 * rolling (a carjack at walking pace) and the player never walks through the car.
 */

/** A point in the car's frame: metres to its right (`lat`) and towards its nose (`al`). */
export interface CarPoint {
  lat: number;
  al: number;
}

export interface CarShape {
  width: number;
  length: number;
}

/** Gap (m) between the bodywork and the walking line round it. */
const CLEAR = 0.6;
/** Standing this far left of the centre line (m) still counts as the driver's side. */
const DRIVER_BIAS = 0.25;

/**
 * The door to use: the side the player stands on (1 = driver, -1 = passenger: in through the
 * passenger door and slide across). Right in front of or behind the car the driver's door wins.
 */
export function chooseDoorSide(lat: number): 1 | -1 {
  return lat >= -DRIVER_BIAS ? 1 : -1;
}

/**
 * Waypoints from `from` to `door` (both in the car's frame) that keep clear of the bodywork: straight
 * there from the side, round the nearer corner from in front or behind. The last point is the door.
 */
export function entryPath(from: CarPoint, door: CarPoint, car: CarShape): CarPoint[] {
  const halfW = car.width / 2;
  const halfL = car.length / 2;
  const side = door.lat >= 0 ? 1 : -1;
  const beyondEnd = Math.abs(from.al) > halfL - 0.1;
  const besideBody = from.lat * side > halfW + 0.2;
  if (beyondEnd && !besideBody) {
    // In front of / behind the car: step out to the corner on the door's side first.
    const corner = { lat: side * (halfW + CLEAR), al: Math.sign(from.al) * (halfL + CLEAR * 0.5) };
    return [corner, door];
  }
  if (!besideBody && Math.abs(from.al) <= halfL) {
    // Squeezed against the far side (or inside the box after a shove): out to the door's side line first.
    return [{ lat: side * (halfW + CLEAR), al: from.al }, door];
  }
  return [door];
}

/** Length (m) of a path that starts at `from`. */
export function pathLength(from: CarPoint, path: readonly CarPoint[]): number {
  let len = 0;
  let a = from;
  for (const b of path) {
    len += Math.hypot(b.lat - a.lat, b.al - a.al);
    a = b;
  }
  return len;
}

/** Point a fraction `u` (0..1) of the way along the path from `from`. */
export function pointAlong(from: CarPoint, path: readonly CarPoint[], u: number): CarPoint {
  const total = pathLength(from, path);
  if (total <= 1e-6 || path.length === 0) return path[path.length - 1] ?? from;
  let left = Math.max(0, Math.min(1, u)) * total;
  let a = from;
  for (const b of path) {
    const seg = Math.hypot(b.lat - a.lat, b.al - a.al);
    if (left <= seg && seg > 0) {
      const k = left / seg;
      return { lat: a.lat + (b.lat - a.lat) * k, al: a.al + (b.al - a.al) * k };
    }
    left -= seg;
    a = b;
  }
  return path[path.length - 1]!;
}

/** Walking speed (m/s) to the door: a brisk trot, so the stride reads as a run, not a slide. */
export const ENTER_WALK_SPEED = 5.5;
/** Share of an enter / steal / break-in action that is the walk when the door is close (the old timing). */
export const ENTER_WALK_SHARE = 0.3;

/**
 * Timing of an enter action: the walk takes at least its old share of `base`, longer when the door is
 * far (at ENTER_WALK_SPEED); the door part (open, duck in, or the yank) keeps the rest of `base`.
 */
export function entryTiming(base: number, walkMetres: number): { walk: number; door: number } {
  if (base <= 0) return { walk: 0, door: 0 };
  return { walk: Math.max(base * ENTER_WALK_SHARE, walkMetres / ENTER_WALK_SPEED), door: base * (1 - ENTER_WALK_SHARE) };
}

/**
 * Animation progress (0..1) of the enter clip: the walk fills the first ENTER_WALK_SHARE (the clip's
 * approach, where the legs follow the real speed) and the door part the rest, however long the walk.
 */
export function entryProgress(elapsed: number, t: { walk: number; door: number }): number {
  if (t.walk + t.door <= 0) return 1;
  if (elapsed < t.walk) return ENTER_WALK_SHARE * (elapsed / Math.max(t.walk, 1e-6));
  return Math.min(1, ENTER_WALK_SHARE + (1 - ENTER_WALK_SHARE) * ((elapsed - t.walk) / Math.max(t.door, 1e-6)));
}

/** World point -> car frame. `right` and `fwd` are the car's unit vectors on the ground. */
export function toCarFrame(p: V2, centre: V2, right: V2, fwd: V2): CarPoint {
  const dx = p.x - centre.x;
  const dz = p.z - centre.z;
  return { lat: dx * right.x + dz * right.z, al: dx * fwd.x + dz * fwd.z };
}

/** Car frame -> world point. */
export function fromCarFrame(q: CarPoint, centre: V2, right: V2, fwd: V2): V2 {
  return { x: centre.x + right.x * q.lat + fwd.x * q.al, z: centre.z + right.z * q.lat + fwd.z * q.al };
}
